/*
 * Where a terminal's cell grid sits in its pane (docs/terminal.md, "Padding";
 * owner's request of 2026-10-22: the gap around mc was wider on the right and
 * at the bottom). xterm draws whole cells only, so a pane is almost never an
 * exact multiple of the cell: the grid gets at least `PANE_PADDING` on every
 * side and the remainder is split evenly, left and right, top and bottom.
 * The pane itself is painted in the terminal theme's background, so the
 * gaps never show as light strips.
 */

/** The least gap between the pane's edge and the grid, on every side, in CSS pixels. */
export const PANE_PADDING = 10;

/** The fewest columns and rows a terminal is given, whatever the pane (as xterm's fit addon). */
const MIN_COLS = 2;
const MIN_ROWS = 1;

export interface Size {
  width: number;
  height: number;
}

export interface PaneGeometry {
  cols: number;
  rows: number;
  /** The gap left of the grid, whole pixels; the gap on the right differs by a pixel at most. */
  left: number;
  /** The gap above the grid, whole pixels; the gap below differs by a pixel at most. */
  top: number;
}

/**
 * The grid for a pane of `pane` CSS pixels with cells of `cell`: as many
 * columns and rows as fit inside the padding, centred. Null while the pane
 * or the cell has no size yet (not laid out, the font not measured).
 */
export function paneGeometry(
  pane: Size,
  cell: Size,
  padding: number = PANE_PADDING,
): PaneGeometry | null {
  if (pane.width <= 0 || pane.height <= 0 || cell.width <= 0 || cell.height <= 0) return null;
  const cols = Math.max(MIN_COLS, Math.floor((pane.width - 2 * padding) / cell.width));
  const rows = Math.max(MIN_ROWS, Math.floor((pane.height - 2 * padding) / cell.height));
  return {
    cols,
    rows,
    left: Math.max(0, Math.round((pane.width - cols * cell.width) / 2)),
    top: Math.max(0, Math.round((pane.height - rows * cell.height) / 2)),
  };
}
